import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';

interface FloatingBackButtonProps {
  /** Custom fallback route if history is empty */
  fallbackRoute?: string;
  /** Hide on specific routes */
  hideOnRoutes?: string[];
  /** Show only on specific routes */
  showOnRoutes?: string[];
  /** Custom position (default: bottom-left) */
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /** Show text label (default: true on desktop, false on mobile) */
  showLabel?: boolean | 'desktop-only';
  /** Custom className for styling */
  className?: string;
}

export function FloatingBackButton({
  fallbackRoute = '/',
  hideOnRoutes = ['/', '/login', '/signup'],
  showOnRoutes,
  position = 'bottom-left',
  showLabel = 'desktop-only',
  className,
}: FloatingBackButtonProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Determine if button should be visible
    const currentPath = location.pathname;

    // If showOnRoutes is specified, only show on those routes
    if (showOnRoutes && showOnRoutes.length > 0) {
      setIsVisible(showOnRoutes.some(route => currentPath.startsWith(route)));
      return;
    }

    // Otherwise, show everywhere except hideOnRoutes
    const shouldHide = hideOnRoutes.some(route => currentPath === route);
    setIsVisible(!shouldHide);
  }, [location.pathname, hideOnRoutes, showOnRoutes]);

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // No history, go to fallback route
      navigate(fallbackRoute);
    }
  };

  if (!isVisible) return null;

  const positionClasses = {
    'bottom-left': 'bottom-24 left-4 sm:bottom-8 sm:left-8',
    'bottom-right': 'bottom-24 right-4 sm:bottom-8 sm:right-8',
    'top-left': 'top-20 left-4 sm:top-24 sm:left-8',
    'top-right': 'top-20 right-4 sm:top-24 sm:right-8',
  };

  const labelVisible = showLabel === true || (showLabel === 'desktop-only' && 'sm:inline-flex');

  return (
    <button
      onClick={handleBack}
      className={`
        fixed ${positionClasses[position]} z-40
        group
        flex items-center gap-2
        ${showLabel ? 'px-4 py-3' : 'w-12 h-12 sm:w-14 sm:h-14 justify-center'}
        bg-gradient-to-r from-orange-500 to-amber-500
        hover:from-orange-600 hover:to-amber-600
        text-white font-semibold
        rounded-full
        shadow-lg hover:shadow-xl
        transition-all duration-200
        transform hover:scale-105 active:scale-95
        ${className || ''}
      `}
      aria-label="Go back"
      title="Go back"
    >
      <ArrowLeft className={`
        ${showLabel ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6'}
        group-hover:-translate-x-1 transition-transform duration-200
      `} />
      {showLabel === true && <span>Back</span>}
      {showLabel === 'desktop-only' && <span className="hidden sm:inline">Back</span>}
    </button>
  );
}

/**
 * Usage Examples:
 *
 * 1. Basic usage (default behavior):
 * <FloatingBackButton />
 *
 * 2. Show only on specific routes:
 * <FloatingBackButton showOnRoutes={['/jobseeker', '/employer']} />
 *
 * 3. Custom position and fallback:
 * <FloatingBackButton
 *   position="bottom-right"
 *   fallbackRoute="/dashboard"
 * />
 *
 * 4. Hide on multiple routes:
 * <FloatingBackButton
 *   hideOnRoutes={['/', '/login', '/signup', '/swipe']}
 * />
 *
 * 5. No label (icon only):
 * <FloatingBackButton showLabel={false} />
 *
 * 6. Always show label:
 * <FloatingBackButton showLabel={true} />
 */
